import { Dialog, DialogButtons, DialogContent, DialogTitle } from '@/Components/Dialog/Dialog';
import { WorklistColumnViewParam, WorklistFilterViewParam, type WorklistViewParams } from '@/Models/GenericWorklist/WorklistParams';
import { DobButton, DobDualListBox, DobTab, DobTabs } from '@dedalusdiit/dob-components';
import React, { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Styles from './ColumnsSetupModal.module.scss';
import { applyDualListSelectionToViewParams, toActiveDualListItems, toSortedDualListOptions } from '../Logic/WorklistDualListMapping';
import { WORKLIST_SETUP_DIALOG_COLUMN_TAB, WORKLIST_SETUP_DIALOG_FILTER_TAB } from '@/Pages/GenericWorklist/Stores/WorklistStoreDefaults';

interface ColumnsSetupDialogProps {
    open: boolean;
    closeCb: () => void;
    setColumnData: (cols: WorklistColumnViewParam[]) => void;
    setFilterData: (cols: WorklistFilterViewParam[]) => void;
    viewData: WorklistViewParams;
    tabIdx: string;
    setCurrTab: (tabIdx: string) => void;
}

function ColumnsSetupDialog({ open, tabIdx, setCurrTab, viewData, setColumnData, setFilterData, closeCb }: Readonly<ColumnsSetupDialogProps>): ReactElement {
    const { t } = useTranslation();

    const filterOptions = useMemo(() => toSortedDualListOptions(viewData.Filters), [viewData.Filters]);
    const activeFilterItems = useMemo(() => toActiveDualListItems(viewData.Filters), [viewData.Filters]);

    const columnOptions = useMemo(() => toSortedDualListOptions(viewData.Columns), [viewData.Columns]);
    const activeColumnItems = useMemo(() => toActiveDualListItems(viewData.Columns), [viewData.Columns]);

    return (
        <Dialog open={open} className={Styles.columnsSetupDialog}>
            <DialogTitle>{t('Settings')}</DialogTitle>
            <DialogContent>
                <DobTabs direction="horizontal" valueTabKey={tabIdx} onChange={setCurrTab}>
                    <DobTab trigger={t('FilterSettings')} internalKey={WORKLIST_SETUP_DIALOG_FILTER_TAB}>
                        <div className={Styles.tabContent}>
                            <DobDualListBox
                                options={filterOptions}
                                activeItems={activeFilterItems}
                                onChange={(updatedFilters) => setFilterData(applyDualListSelectionToViewParams(filterOptions, updatedFilters, activeFilterItems))}
                                optionLabel="label"
                                optionValue="id"
                                translations={{
                                    inactive: t('Available'),
                                    active: t('Active'),
                                    selectAll: t('SelectAll'),
                                    deselectAll: t('DeselectAll'),
                                    selectedMessage: `{X} ${t('TotalSelected')}`
                                }}
                            />
                        </div>
                    </DobTab>
                    <DobTab trigger={t('ColumnSettings')} internalKey={WORKLIST_SETUP_DIALOG_COLUMN_TAB}>
                        <div className={Styles.tabContent}>
                            <DobDualListBox
                                options={columnOptions}
                                activeItems={activeColumnItems}
                                onChange={(updatedColumns) => setColumnData(applyDualListSelectionToViewParams(columnOptions, updatedColumns, activeColumnItems))}
                                optionLabel="label"
                                optionValue="id"
                                translations={{
                                    inactive: t('Available'),
                                    active: t('Active'),
                                    selectAll: t('SelectAll'),
                                    deselectAll: t('DeselectAll'),
                                    selectedMessage: `{X} ${t('TotalSelected')}`
                                }}
                            />
                        </div>
                    </DobTab>
                </DobTabs>
            </DialogContent>
            <DialogButtons>
                <DobButton onClick={closeCb} color="secondary">
                    {t('Close')}
                </DobButton>
            </DialogButtons>
        </Dialog>
    );
}

export {
    ColumnsSetupDialog, type ColumnsSetupDialogProps,
};
